import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import RevealText from "../components/RevealText";
import ScrollReveal from "../components/ScrollReveal";
import TonalPanel from "../components/TonalPanel";

const COVERS = {
  "on-restraint":     { bg: "plum",     tube: "#e6ddcf", cap: "light", sub: "LIP GLOSS" },
  "the-material-list":{ bg: "stone",    tube: "#3a2530", cap: "same",  sub: "LIP GLOSS" },
  "the-ritual":       { bg: "charcoal", tube: "#c9a37a", cap: "dark",  sub: "LIP GLOSS" },
  "future-collections":{ bg: "taupe",   tube: "#141212", cap: "dark",  sub: "LIP BALM"  },
};

export default function JournalArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [all, setAll] = useState([]);

  useEffect(() => {
    api.get(`/journal/${slug}`).then((r) => setArticle(r.data)).catch(() => {});
    api.get(`/journal`).then((r) => setAll(r.data)).catch(() => {});
  }, [slug]);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);

  if (!article) return (
    <div className="min-h-[100svh] flex items-center justify-center">
      <span className="text-[11px] uppercase tracking-editorial text-ink/55">Loading —</span>
    </div>
  );

  const cover = COVERS[article.slug] || COVERS["on-restraint"];
  const next = all.find((x) => x.slug !== article.slug && x.order === (article.order % Math.max(all.length, 1)) + 1);

  return (
    <div className="pt-24 md:pt-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between text-[10px] uppercase tracking-editorial text-ink/60">
        <span>Journal · {article.chapter}</span>
        <Link to="/journal" className="editorial-link">All entries</Link>
      </div>
      <div className="hairline-soft" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10 md:pb-20 text-center">
        <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-6">{article.published} · {article.read_time}</p>
        <RevealText
          as="h1"
          className="font-serif-display text-[10vw] md:text-[6.5vw] leading-[0.95] tracking-[-0.02em] mx-auto max-w-[26ch]"
          lines={[article.title]}
        />
        <p className="mt-6 font-serif-italic italic text-2xl md:text-3xl text-ink/70 max-w-[38ch] mx-auto">
          {article.subtitle}
        </p>
        <p className="mt-6 text-[11px] uppercase tracking-editorial text-ink/60">By {article.author}</p>
      </div>

      <section ref={heroRef} className="relative overflow-hidden max-h-[70vh]">
        <motion.div style={{ y }}>
          <TonalPanel
            bg={cover.bg}
            tubeColor={cover.tube}
            cap={cover.cap}
            sublabel={cover.sub}
            fig={article.chapter}
            caption={article.title}
            chapter={article.published}
            ratio="aspect-[16/9]"
          />
        </motion.div>
      </section>

      <article className="max-w-[720px] mx-auto px-6 md:px-0 py-20 md:py-32">
        <p className="font-serif-display text-3xl md:text-4xl leading-[1.15] mb-12 first-letter:text-[4em] first-letter:font-serif-display first-letter:float-left first-letter:mr-4 first-letter:leading-none first-letter:text-plum">
          {article.excerpt}
        </p>
        {article.body.map((p, i) => (
          <ScrollReveal key={i} delay={i * 0.04}>
            <p className="text-[17px] leading-[1.75] text-ink/85 mb-8">{p}</p>
          </ScrollReveal>
        ))}
        <div className="hairline-soft my-16" />
        <p className="text-[10px] uppercase tracking-editorial text-ink/55 text-center">— House of Korv</p>
      </article>

      {next && (
        <section className="border-t border-ink/15 py-20 md:py-28">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-end justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-6">Next entry</p>
              <Link to={`/journal/${next.slug}`} className="font-serif-display text-4xl md:text-6xl group inline-flex items-baseline gap-6">
                {next.title} <span className="text-[11px] tracking-editorial uppercase text-ink/60">→</span>
              </Link>
            </div>
            <span className="hidden md:inline text-[10px] uppercase tracking-editorial text-ink/60">{next.chapter}</span>
          </div>
        </section>
      )}
    </div>
  );
}
